import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import AgGrid from '../../../shared/agGrid/agGrid.component';
import useGridApi from '../../../shared/hooks/useGridApi.hook';
import useGridToggleFilteredSelection from '../../../shared/hooks/useGridToggleFileteredSelection.hook';

import Button from '../../../shared/button.component';

import ConfirmBalancesClearModal from './confirmBalancesClearModal.component';
import columnDefinitions from './columnDefinitions';
import { CompanyCode } from '../types';
import { CompanyCodePropTypes } from '../propTypes';
import { useModalControl } from '../../../shared/hooks/useModalControl.hook';

type CompanyCodesGridProps = {
  companyCodes: CompanyCode[],
  clearBsmBalances: (companyCodes: string[]) => void,
  isLoadingClearBsmBalances: boolean,
}

const CompanyCodesGrid: React.FC<CompanyCodesGridProps> = ({
  companyCodes,
  clearBsmBalances,
  isLoadingClearBsmBalances,
}) => {
  const { gridApi, onGridReady } = useGridApi();
  const { selectAllFiltered, unselectAllFiltered } = useGridToggleFilteredSelection(gridApi);
  const {
    showModal: showConfirmModal,
    hideModal: hideConfirmModal,
    isModalVisible: isConfirmModalVisible,
  } = useModalControl();

  const [selectedCount, setSelectedCount] = useState(0);

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridApi?.getSelectedNodes() || [];
    setSelectedCount(selectedNodes.length);
  }, [gridApi]);

  useEffect(() => {
    gridApi?.setGridOption('columnDefs', columnDefinitions);
  }, [gridApi]);

  const onClearBsmBalances = useCallback(async () => {
    const selectedCompanyCodes = gridApi?.getSelectedRows().map(({ cc }) => cc) as string[];
    await clearBsmBalances(selectedCompanyCodes);
    hideConfirmModal();
    unselectAllFiltered();
  }, [gridApi, clearBsmBalances, hideConfirmModal, unselectAllFiltered]);

  return (
    <div>
      <h6>Clear BSM Balances</h6>
      <AgGrid
        rowData={companyCodes}
        isGridLoading={isLoadingClearBsmBalances}
        onGridReady={onGridReady}
        suppressRowClickSelection
        rowSelection="multiple"
        onSelectionChanged={onSelectionChanged}
        withSearchBar
      />
      <ConfirmBalancesClearModal
        hideModal={hideConfirmModal}
        isVisible={isConfirmModalVisible}
        clearBsmBalances={onClearBsmBalances}
        isLoadingClearBsmBalances={isLoadingClearBsmBalances}
      />
      <div className="row no-gutters page-content-container d-flex">
        <div>
          <Button
            onClick={selectAllFiltered}
            kind="secondary"
            disabled={isLoadingClearBsmBalances}
          >
            Select all
          </Button>
          <Button
            onClick={unselectAllFiltered}
            className="ml-2"
            kind="secondary"
            disabled={isLoadingClearBsmBalances || selectedCount === 0}
          >
            Clear all
          </Button>
        </div>
        <div className="ml-auto">
          <Button
            onClick={showConfirmModal}
            className="ml-2"
            disabled={isLoadingClearBsmBalances || selectedCount === 0}
          >
            Submit Selected
          </Button>
        </div>
      </div>
    </div>
  );
};

CompanyCodesGrid.propTypes = {
  companyCodes: PropTypes.arrayOf(CompanyCodePropTypes.isRequired).isRequired,
  isLoadingClearBsmBalances: PropTypes.bool.isRequired,
  clearBsmBalances: PropTypes.func.isRequired,
};

export default CompanyCodesGrid;
